import { CheckCircle2Icon, TriangleAlertIcon } from 'lucide-react'

import { type ComplianceCalculations } from 'components/hooks/quotes/useComplianceCalculations'
import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { Tooltip } from 'slab'
import { formatEmployeeContributionPercentage } from 'utils/quote_calculations'

type Props = {
  maxEmployeeContributionPercentage?: number
  isLoading: boolean
} & Pick<ComplianceCalculations, 'employeesWithUnaffordablePlans' | 'ratingAreasWithUnfairAllowances'>

export default function ComplianceChecks ({
  maxEmployeeContributionPercentage,
  employeesWithUnaffordablePlans,
  ratingAreasWithUnfairAllowances,
  isLoading,
}: Props) {
  if (isLoading) {
    return (
      <div className='flex flex-col gap-2'>
        <SkeletonItem heightClass='h-4' widthClass='w-full' />
        <SkeletonItem heightClass='h-4' widthClass='w-full' />
      </div>
    )
  }

  return (
    <div className='flex flex-col gap-2 w-'>
      <div className='flex items-center gap-2'>
        {employeesWithUnaffordablePlans.length === 0
          ? (
            <CheckCircle2Icon className='w-4 h-4 text-green-400' />
            )
          : (
            <TriangleAlertIcon className='w-4 h-4 text-red-400' />
            )}
        <Tooltip content={`In order for an allowance to be considered affordable, employees must be able to purchase the lowest cost silver plan without paying more than ${formatEmployeeContributionPercentage(maxEmployeeContributionPercentage)}% of their annual income.`}>
          <span className='text-sm cursor-help'>
            {employeesWithUnaffordablePlans.length === 0
              ? 'All employees have affordable coverage'
              : `${employeesWithUnaffordablePlans.length} employee${employeesWithUnaffordablePlans.length === 1 ? ' has' : 's have'} unaffordable coverage`}
          </span>
        </Tooltip>
      </div>
      <div className='flex items-center gap-2'>
        {ratingAreasWithUnfairAllowances.length === 0
          ? (
            <CheckCircle2Icon className='w-4 h-4 text-green-400' />
            )
          : (
            <TriangleAlertIcon className='w-4 h-4 text-red-400' />
            )}
        <Tooltip content="In order for an allowance to be considered fair, the oldest employee's allowance can be no more than 3x the youngest employee's allowance in a given rating area.">
          <span className='text-sm cursor-help'>
            {ratingAreasWithUnfairAllowances.length === 0
              ? 'Fair allowance rule'
              : `${ratingAreasWithUnfairAllowances.length} rating area${ratingAreasWithUnfairAllowances.length === 1 ? ' has' : 's have'} an unfair allowance structure`}
          </span>
        </Tooltip>
      </div>
    </div>
  )
}
